import React, { Fragment } from 'react';
import fr from 'facet-rest';
import AccountsHeading from 'Components/Blueprint/UI/Accounts/Heading';
import AccountsTable from 'Components/Blueprint/UI/Accounts/Table';
import AccountsTableInner from 'Components/Blueprint/UI/Accounts/TableInner';
import ShadowBox from 'Components/UI/ShadowBox';
import EditButton from 'Components/Blueprint/UI/EditButton';
import { currency } from 'Helpers/Formats';
import Currency from 'Components/Format/Currency';
import BlueprintBalanceView from 'Components/Blueprint/UI/BlueprintBalanceForResources';
import Content from 'Components/Blueprint/UI/Content';
import PropTypes from 'prop-types';
import { noop } from 'Helpers/PropTypes';
import { openURLInNewTab } from 'Helpers/Navigation';

const View = ({
  bankAccounts,
  nonRetirementAccounts,
  personalRetirementAccounts,
  employerSponsoredPlans,
  healthSavingsAccounts,
  getEditLink,
}) => {
  return (
    <div className="investment-assets">
      {bankAccounts.length > 0
          || nonRetirementAccounts.length > 0
          || personalRetirementAccounts.length > 0
          || employerSponsoredPlans.length > 0
          || healthSavingsAccounts.length > 0 ? (
            <Fragment>
              <AccountsHeading title="Investment Assets">
                <AccountsTable className="accounts-client-profile">
                  <tr className="headings">
                    <td className="annual">Additions [w/d]</td>
                    <td className="employer">Employer</td>
                    <td className="balance">Balance</td>
                    <td className="institution">Institution</td>
                    <td className="type">Type</td>
                    <td className="owner">Owner</td>
                    <td className="notes">Notes</td>
                  </tr>
                </AccountsTable>
              </AccountsHeading>
              {/*
            |--------------------------------------------------------------------------
            | Bank/CU Accounts
            |--------------------------------------------------------------------------
            */}
              {bankAccounts.length > 0 && (
              <Fragment>
                <AccountsTableInner>
                  <AccountsTable
                    subtitle="Bank/CU Accounts"
                    editLink={getEditLink('investmentAssets', 'bankAccounts')}
                    editTooltip="Edit Bank/CU Accounts"
                  />
                  <ShadowBox>
                    <AccountsTable className="accounts-client-profile">
                      {bankAccounts.map(account => (
                        <tr key={`${account.id()}ba`} className="data">
                          <td className="annual">
                            <div className="annual-button-amount">
                              <EditButton
                                title="Edit Bank Account"
                                onClick={() => {
                                  account.withRelationship(
                                    fr.relationships.edit.name,
                                    (rel) => openURLInNewTab(rel.link('related').url()),
                                    () => null,
                                  );
                                }}
                              />
                              {account.attr('annualAdditions') ? currency(parseFloat(account.attr('annualAdditions')), true) : ''}
                            </div>
                          </td>
                          <td className="employer">N/A</td>
                          <BlueprintBalanceView account={account} />
                          <td className="institution">
                            {account.attr('institution')}
                          </td>
                          <td className="type">
                            {account.selectedOptions('type', 'prompt')}
                          </td>
                          <td className="owner">{account.attr('owner')}</td>
                          <td className="notes">{account.attr('notes')}</td>
                        </tr>
                      ))}
                    </AccountsTable>
                  </ShadowBox>
                </AccountsTableInner>
              </Fragment>
              )}
              {/*
            |--------------------------------------------------------------------------
            | Non-qualified Accounts
            |--------------------------------------------------------------------------
            */}
              {nonRetirementAccounts.length > 0 && (
              <Fragment>
                <AccountsTableInner>
                  <AccountsTable
                    subtitle="Non-qualified Accounts"
                    editLink={getEditLink('investmentAssets', 'nonRetirementAccounts')}
                    editTooltip="Edit Non-qualified Accounts"
                  />
                  <ShadowBox>
                    <AccountsTable className="accounts-client-profile">
                      {nonRetirementAccounts.map(account => (
                        <tr key={`${account.id()}nra`} className="data">
                          <td className="annual">
                            <EditButton
                              title="Edit Non-Qualified Account"
                              onClick={() => {
                                account.withRelationship(
                                  fr.relationships.edit.name,
                                  (rel) => openURLInNewTab(rel.link('related').url()),
                                  () => null,
                                );
                              }}
                            />
                            <Currency
                              amount={account.attr('annualAdditions')}
                            />
                          </td>
                          <td className="employer">N/A</td>
                          <BlueprintBalanceView account={account} />
                          <td className="institution">
                            {account.attr('institution')}
                          </td>
                          <td className="type">
                            {account.selectedOptions('type', 'prompt')}
                          </td>
                          <td className="owner">{account.attr('owner')}</td>
                          <td className="notes">{account.attr('notes')}</td>
                        </tr>
                      ))}
                    </AccountsTable>
                  </ShadowBox>
                </AccountsTableInner>
              </Fragment>
              )}
              {/*
            |--------------------------------------------------------------------------
            | Retirement (IRAs / Roth IRAs)
            |--------------------------------------------------------------------------
            */}
              {personalRetirementAccounts.length > 0 && (
              <Fragment>
                <AccountsTableInner>
                  <AccountsTable
                    subtitle="Retirement (IRAs / Roth IRAs)"
                    editLink={getEditLink('investmentAssets', 'personalRetirementAccounts')}
                    editTooltip="Edit Retirement (IRAs / Roth IRAs)"
                  />
                  <ShadowBox>
                    <AccountsTable className="accounts-client-profile">
                      {personalRetirementAccounts.map(account => (
                        <tr key={`${account.id()}pra`} className="data">
                          <td className="annual">
                            <EditButton
                              title="Edit Retirement Account"
                              onClick={() => {
                                account.withRelationship(
                                  fr.relationships.edit.name,
                                  (rel) => openURLInNewTab(rel.link('related').url()),
                                  () => null,
                                );
                              }}
                            />
                            <Currency
                              amount={account.attr('annualAdditions')}
                            />
                          </td>
                          <td className="employer">N/A</td>
                          <BlueprintBalanceView account={account} />
                          <td className="institution">
                            {account.attr('institution')}
                          </td>
                          <td className="type">
                            {account.selectedOptions('type', 'prompt')}
                          </td>
                          <td className="owner">{account.attr('owner')}</td>
                          <td className="notes">{account.attr('notes')}</td>
                        </tr>
                      ))}
                    </AccountsTable>
                  </ShadowBox>
                </AccountsTableInner>
              </Fragment>
              )}
              {/*
            |--------------------------------------------------------------------------
            | Employer Sponsored Plans
            |--------------------------------------------------------------------------
            */}
              {employerSponsoredPlans.length > 0 && (
              <Fragment>
                <AccountsTableInner>
                  <AccountsTable
                    subtitle="Employer Sponsored Plans"
                    editLink={getEditLink('investmentAssets', 'employerSponsoredPlans')}
                    editTooltip="Edit Employer Sponsored Plans"
                  />
                  <ShadowBox>
                    <AccountsTable className="accounts-client-profile">
                      {employerSponsoredPlans.map(account => (
                        <tr key={`${account.id()}esp`} className="data">
                          <td className="annual">
                            <EditButton
                              title="Edit Employer Sponsored Plan Account"
                              onClick={() => {
                                account.withRelationship(
                                  fr.relationships.edit.name,
                                  (rel) => openURLInNewTab(rel.link('related').url()),
                                  () => null,
                                );
                              }}
                            />
                            <Currency
                              amount={account.attr('employeeAdditions')}
                            />
                          </td>
                          <td className="employer">
                            {account.attr('employerAdditions') !== null && (
                            <Currency
                              amount={account.attr('employerAdditions')}
                            />
                            )}
                          </td>
                          <BlueprintBalanceView account={account} />
                          <td className="institution">
                            {account.attr('institution')}
                          </td>
                          <td className="type">
                            {account.selectedOptions('type', 'prompt')}
                          </td>
                          <td className="owner">{account.attr('owner')}</td>
                          <td className="notes">{account.attr('notes')}</td>
                        </tr>
                      ))}
                    </AccountsTable>
                  </ShadowBox>
                </AccountsTableInner>
              </Fragment>
              )}
              {/*
            |--------------------------------------------------------------------------
            | Health Savings Accounts
            |--------------------------------------------------------------------------
            */}
              {healthSavingsAccounts.length > 0 && (
              <Fragment>
                <AccountsTableInner>
                  <AccountsTable
                    subtitle="Health Savings Accounts"
                    editLink={getEditLink('investmentAssets', 'healthSavingsAccounts')}
                    editTooltip="Edit Health Savings Accounts"
                  />
                  <ShadowBox>
                    <AccountsTable className="accounts-client-profile">
                      {healthSavingsAccounts.map(account => (
                        <tr key={`${account.id()}hsa`} className="data">
                          <td className="annual">
                            <EditButton
                              title="Edit Health Savings Account"
                              onClick={() => {
                                account.withRelationship(
                                  fr.relationships.edit.name,
                                  (rel) => openURLInNewTab(rel.link('related').url()),
                                  () => null,
                                );
                              }}
                            />
                          </td>
                          <td className="employer" />
                          <BlueprintBalanceView account={account} />
                          <td className="institution">
                            {account.attr('institution')}
                          </td>
                          <td className="type">
                            {account.selectedOptions('type', 'prompt')}
                          </td>
                          <td className="owner">{account.attr('owner')}</td>
                          <td className="notes">{account.attr('notes')}</td>
                        </tr>
                      ))}
                    </AccountsTable>
                  </ShadowBox>
                </AccountsTableInner>
              </Fragment>
              )}
            </Fragment>
        ) : (
          <Fragment>
            <AccountsHeading title="Investment Assets" />
            <Content className="double-side-padding no-top-padding  ">
              <p>No investment assets.</p>
            </Content>
          </Fragment>
        )}
    </div>
  );
};
View.propTypes = {
  bankAccounts: PropTypes.array,
  nonRetirementAccounts: PropTypes.array,
  personalRetirementAccounts: PropTypes.array,
  employerSponsoredPlans: PropTypes.array,
  healthSavingsAccounts: PropTypes.array,
  getEditLink: PropTypes.func,
};
View.defaultProps = {
  getEditLink: noop,
  bankAccounts: [],
  nonRetirementAccounts: [],
  personalRetirementAccounts: [],
  employerSponsoredPlans: [],
  healthSavingsAccounts: [],
};

export default View;
